import { useState, useEffect, useCallback, useRef } from "react";
import { EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { EntityManagerOtherParams, WidgetParamKey, WidgetParamKeys } from "@/types/entity";
import { useHistory } from "react-router-dom";
import ViewManager from "@/other/feature/views/ViewManager";
import { debounce } from "@/other/utils/otherUtils";
import { toast } from "sonner";

const EVENT_NAME = "entityManager:urlChanged";

interface LocationState {
  isDashboard?: boolean;
  [key: string]: unknown;
}

const getQueryParams = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const entityType = params.get("entityType") || "";
  const entityId = params.get("entityId") || "";

  // Extract all widget param keys from WidgetParamKeys enum
  const otherParams: Partial<EntityManagerOtherParams> = {};

  // Iterate through all widget categories (PLANNING, REMINDER, NOTE, CHECKLIST)
  Object.values(WidgetParamKeys).forEach((widgetCategory) => {
    // Iterate through all actions in each category (CREATE, EDIT)
    Object.values(widgetCategory).forEach((paramKey) => {
      const value = params.get(paramKey);
      if (value) {
        // Parse numeric values, keep others as strings
        const parsedValue = !isNaN(Number(value)) ? Number(value) : value;
        (otherParams as Record<string, any>)[paramKey] = parsedValue;
      }
    });
  });

  return {
    entityType,
    entityId,
    otherParams: Object.keys(otherParams).length > 0 ? (otherParams as EntityManagerOtherParams) : null
  };
};

const setQueryParams = (newParams: Record<string, unknown>, history: ReturnType<typeof useHistory>, isReplace: boolean = false) => {
  const url = new URL(window.location.href);

  // Collect all widget param keys for cleanup
  const allWidgetParamKeys: string[] = [];
  Object.values(WidgetParamKeys).forEach((widgetCategory) => {
    Object.values(widgetCategory).forEach((paramKey) => {
      allWidgetParamKeys.push(paramKey);
    });
  });

  Object.entries(newParams).forEach(([key, value]) => {
    // Special handling for otherParams - flatten into individual URL params
    if (key === "otherParams") {
      // First, remove all existing widget params from URL
      allWidgetParamKeys.forEach((paramKey) => {
        url.searchParams.delete(paramKey);
      });

      // Then, add the new widget params if otherParams is not null
      if (value && typeof value === "object") {
        Object.entries(value as EntityManagerOtherParams).forEach(([paramKey, paramValue]) => {
          if (paramValue !== null && paramValue !== undefined) {
            url.searchParams.set(paramKey, String(paramValue));
          }
        });
      }
    } else if (value === null || value === undefined || value === "") {
      url.searchParams.delete(key);
    } else if (typeof value === "object") {
      url.searchParams.set(key, JSON.stringify(value));
    } else {
      url.searchParams.set(key, String(value));
    }
  });

  const locationState = history?.location?.state as LocationState | undefined;

  const historyConfig = {
    pathname: url.pathname,
    search: url.search,
    state: {
      ...locationState,
      isDashboard: Boolean(newParams.entityType) && Boolean(newParams.entityId)
    }
  };

  if (locationState?.isDashboard || isReplace) {
    history.replace(historyConfig);
  } else {
    history.push(historyConfig);
  }

  window.dispatchEvent(new Event(EVENT_NAME));
};

export const useEntityManager = ({viewManager}: {viewManager: ViewManager}) => {
  const history = useHistory();
  const isRefreshingRoute = useRef<boolean>(false);

  const [state, setState] = useState(() => {
    /* Initialize the state with the query params */
    const params = getQueryParams();
    return params;
  });

  const isOpen = Boolean(state.entityType) && Boolean(state.entityId);

  /* This useEffect syncs the 'react state' with the URLs query params changes, as you see when user clicks browser back/forward button that does not update react state therefore we have to listen to events & do it here. */
  useEffect(() => {
    const sync = () => setState(getQueryParams());

    /* sync the 'history' with query params initially, this works in case of directly pasting the url in the browser */
    setQueryParams({...state}, history)

    window.addEventListener("popstate", sync);
    window.addEventListener(EVENT_NAME, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(EVENT_NAME, sync);
    };
  }, []);


  /* This useeffect is tracking changes in view manager view changes & if view changes then only it closes the current dashboard */
  useEffect(() => {
    if (viewManager && viewManager.renderState.selectedView) {
      const detacher = history.listen((e: any) => {
        /** The refresh route flag prevents infinite loop when 'replaceRoute' in the setQueryParam function triggers this listener again */
        if (isOpen && !e.state?.isDashboard && !isRefreshingRoute.current) {
          isRefreshingRoute.current = true;
          closeEntityManager();
          /* The timeout is used to prevent any pending UI state updates like the 'dashboard drawer' from closing */
          setTimeout(() => {
            isRefreshingRoute.current = false;
          })
        }
      });
      return () => detacher();
    }
    return undefined;
  }, [viewManager, history, isOpen, isRefreshingRoute]);

  const updateState = useCallback(
    (newPartial: Record<string, unknown>) => {
      const next = { ...state, ...newPartial };
      setQueryParams(next, history);
      setState(next);
    },
    [state, history]
  );

  const openEntityManager = useCallback(
    ({ entityType, entityId, otherParams }: { entityType: EntityTypeKey; entityId: string | number; otherParams?: EntityManagerOtherParams | null }) => {
      updateState({
        entityType,
        entityId: String(entityId),
        otherParams: otherParams && Object.values(otherParams).length > 0 ? otherParams : null
      });
    },
    [updateState]
  );

  const closeEntityManager = useCallback(() => {
    // Always use setQueryParams with replace to avoid history navigation issues.
    // Previously, when isForce=false, we used history.goBack() which caused issues
    // when ViewManager's selectLastSelectedView created intermediate history entries.
    // Now we always replace the current URL to remove entity manager params,
    // which ensures we stay on the current view without unexpected navigation.
    setQueryParams({entityType: null, entityId: null, otherParams: null}, history, true);
  }, [history]);

  const debouncedClearOtherParams = useCallback(debounce(() => {
    updateState({ otherParams: null });
  }, 1000), [updateState]);

  const callParamAction = useCallback((key: WidgetParamKey, callback: (value: any) => boolean | void) => {
    if (state.otherParams && key in state.otherParams && callback(state.otherParams[key])) {
      debouncedClearOtherParams();
    }
  }, [state, debouncedClearOtherParams]);

  const copyEntityLink = useCallback(async (
    widgetParamKey?: WidgetParamKey,
    widgetParamValue?: string | number,
    customMessage?: string,
    skipCopy: boolean = false,
    entityConfig?: {
      entityType: EntityTypeKey;
      entityId: string | number;
    }
  ) => {
    const url = new URL(window.location.href);

    // Clear existing search params and rebuild
    url.search = '';

    // Add entity params
    if (entityConfig?.entityType || state.entityType) {
      url.searchParams.set('entityType', String(entityConfig?.entityType || state.entityType || ''));
    }
    if (entityConfig?.entityId || state.entityId) {
      url.searchParams.set('entityId', String(entityConfig?.entityId || state.entityId || ''));
    }

    // Add widget param if provided
    if (widgetParamKey && widgetParamValue !== undefined && widgetParamValue !== null) {
      url.searchParams.set(widgetParamKey, String(widgetParamValue));
    }

    const generatedUrl = url.toString();

    // Skip copy if requested (just return URL)
    if (skipCopy) {
      return generatedUrl;
    }

    try {
      await navigator.clipboard.writeText(generatedUrl);
      // Only show toast if customMessage is not explicitly empty string
      if (customMessage !== "") {
        const defaultMessage = widgetParamKey
          ? "Link copied to clipboard"
          : "Entity link copied to clipboard";
        toast.success(customMessage || defaultMessage);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy link to clipboard');
    }

    return generatedUrl;
  }, [state.entityType, state.entityId]);

  return {
    entityType: state.entityType || null,
    entityId: state.entityId || null,
    otherParams: state.otherParams ?? null,
    openEntityManager,
    closeEntityManager,
    isOpen,
    isRefreshingRoute,
    callParamAction,
    copyEntityLink
  };
};
